import { useEffect, useState, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import { useChat } from "../context/ChatContext";
import { UserContext } from "../context/UserContext";
import {
  FaPaperPlane,
  FaTimes,
  FaSync,
  FaArrowLeft,
  FaComments,
} from "react-icons/fa";
import EmptyState from "../components/EmptyState";
import PageWithRail from "../components/PageWithRail";
import {
  getAvatarUrl as resolveAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import "./Chat.css";

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

function Chat() {
  const {
    conversations,
    conversationsPagination,
    loadingMoreConversations,
    loadMoreConversations,
    currentConversation,
    messages,
    messagesHasMore,
    loadingOlderMessages,
    loadOlderMessages,
    isConnected,
    fetchConversations,
    fetchMessages,
    sendMessage,
    selectConversation,
    leaveConversation,
    markAsRead,
  } = useChat();

  const { user } = useContext(UserContext);
  const [messageInput, setMessageInput] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  // Survive remounts: conversation lives in ChatContext, so restore thread view if one is active
  const [mobileShowThread, setMobileShowThread] = useState(
    () => Boolean(currentConversation)
  );
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(() => {
      fetchConversations();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (!currentConversation) return undefined;
    const interval = setInterval(() => {
      fetchMessages(currentConversation.conversationId);
    }, 60000);
    return () => clearInterval(interval);
  }, [currentConversation, fetchMessages]);

  const scrollToBottom = (smooth = true) => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "end",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      const id = setTimeout(() => scrollToBottom(false), 80);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [currentConversation]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !currentConversation?.otherUser?._id) return;

    const content = messageInput;
    setMessageInput("");
    try {
      await sendMessage(currentConversation.otherUser._id, content);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageInput(content);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatListTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay) {
      return formatTime(timestamp);
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const startOfMsg = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const dayDiff = Math.round(
      (startOfToday - startOfMsg) / (1000 * 60 * 60 * 24)
    );

    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Yesterday";
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDayKey = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const openConversation = async (conversation) => {
    if (!conversation?.otherUser?._id) return;
    setIsLoadingMessages(true);
    setMobileShowThread(true);
    try {
      await selectConversation(conversation);
      await fetchMessages(conversation.conversationId);
    } finally {
      setIsLoadingMessages(false);
      setTimeout(() => scrollToBottom(false), 80);
    }
  };

  const handleLeaveConversation = () => {
    leaveConversation();
    setMobileShowThread(false);
  };

  const handleBackToInbox = () => {
    setMobileShowThread(false);
  };

  const getAvatarUrl = (avatar) => resolveAvatarUrl(avatar);

  if (!user) {
    return (
      <PageWithRail title="Messages">
        <div className="chat-page">
          <div className="page-head">
            <h1 className="page-head__title">Messages</h1>
            <p className="page-head__subtitle">
              Chat with poets across PeerPoetry
            </p>
          </div>
          <div className="chat-login peer-home-page-card">
            <h2 className="chat-login__title">Sign in to message</h2>
            <p className="chat-login__text">
              Log in to see your conversations and write to other poets.
            </p>
            <Link to="/login" state={{ from: "/chat" }} className="peer-home-btn-primary">
              Log in
            </Link>
          </div>
        </div>
      </PageWithRail>
    );
  }

  const otherUser = currentConversation?.otherUser;
  const showThread = Boolean(currentConversation) && mobileShowThread;

  return (
    <PageWithRail title="Messages">
      <div className="chat-page">
        <div className="page-head">
          <h1 className="page-head__title">Messages</h1>
          <p className="page-head__subtitle">
            Chat with poets across PeerPoetry
          </p>
        </div>

        <div className="chat-shell">
          <aside
            className={`chat-inbox${
              showThread ? " chat-inbox--hidden-mobile" : ""
            }`}
            aria-label="Conversations"
          >
            <div className="chat-inbox__head">
              <h2 className="chat-inbox__title">Inbox</h2>
              <div className="chat-inbox__status">
                <span
                  className={`chat-inbox__status-dot${
                    isConnected ? "" : " chat-inbox__status-dot--offline"
                  }`}
                  aria-hidden="true"
                />
                <span>{isConnected ? "Online" : "Offline"}</span>
                {!isConnected && (
                  <button
                    type="button"
                    className="chat-inbox__reconnect"
                    onClick={() => window.location.reload()}
                  >
                    Reconnect
                  </button>
                )}
              </div>
            </div>

            <div className="chat-inbox__list">
              {conversations.length === 0 ? (
                <div className="chat-inbox__empty">
                  <EmptyState
                    icon="inbox"
                    title="No conversations yet"
                    message="Open a poet’s profile and start a chat to see threads here."
                    className="py-6"
                  />
                </div>
              ) : (
                conversations.map((conversation) => {
                  const other =
                    conversation?.otherUser || {
                      _id: null,
                      username: "deleted",
                      name: "Deleted user",
                      avatar: "",
                    };
                  const isDeletedUser = !conversation?.otherUser?._id;
                  const isActive =
                    currentConversation?.conversationId ===
                    conversation.conversationId;

                  return (
                    <button
                      key={conversation._id}
                      type="button"
                      className={`chat-conv${
                        isActive ? " chat-conv--active" : ""
                      }${isDeletedUser ? " chat-conv--disabled" : ""}`}
                      onClick={() => openConversation(conversation)}
                      disabled={isDeletedUser}
                    >
                      <div className="chat-conv__avatar-wrap">
                        <img
                          src={getAvatarUrl(other.avatar)}
                          alt=""
                          className="chat-conv__avatar"
                          onError={avatarOnError}
                        />
                        {conversation.unreadCount > 0 && (
                          <span className="chat-conv__badge">
                            {conversation.unreadCount > 9
                              ? "9+"
                              : conversation.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="chat-conv__body">
                        <div className="chat-conv__row">
                          <h3 className="chat-conv__name">
                            {other.name || other.username}
                          </h3>
                          {conversation.lastMessage && (
                            <span className="chat-conv__time">
                              {formatListTime(conversation.lastMessage.createdAt)}
                            </span>
                          )}
                        </div>
                        {conversation.lastMessage ? (
                          <p className="chat-conv__preview">
                            {conversation.lastMessage.content}
                          </p>
                        ) : (
                          <p className="chat-conv__preview">No messages yet</p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
              {conversationsPagination?.hasNextPage && (
                <div className="chat-inbox__load-more">
                  <button
                    type="button"
                    className="chat-inbox__load-more-btn"
                    disabled={loadingMoreConversations}
                    onClick={() => loadMoreConversations()}
                  >
                    {loadingMoreConversations
                      ? "Loading..."
                      : "Load more conversations"}
                  </button>
                </div>
              )}
            </div>
          </aside>

          <section
            className={`chat-thread${!showThread ? " chat-thread--hidden-mobile" : ""}`}
            aria-label="Conversation"
          >
            {currentConversation ? (
              <>
                <div className="chat-thread__head">
                  <div className="chat-thread__person">
                    <button
                      type="button"
                      className="chat-thread__back"
                      onClick={handleBackToInbox}
                      aria-label="Back to inbox"
                    >
                      <FaArrowLeft aria-hidden="true" />
                    </button>
                    <img
                      src={getAvatarUrl(otherUser?.avatar)}
                      alt=""
                      className="chat-thread__avatar"
                      onError={avatarOnError}
                    />
                    <div className="min-w-0">
                      <h3 className="chat-thread__name">
                        {otherUser?.name ||
                          otherUser?.username ||
                          "Deleted user"}
                      </h3>
                      {otherUser?.username && (
                        <p className="chat-thread__handle">
                          @{otherUser.username}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="chat-thread__actions">
                    <button
                      type="button"
                      className="chat-thread__icon-btn"
                      title="Refresh messages"
                      aria-label="Refresh messages"
                      onClick={async () => {
                        setIsLoadingMessages(true);
                        try {
                          await fetchMessages(
                            currentConversation.conversationId
                          );
                        } finally {
                          setIsLoadingMessages(false);
                          scrollToBottom(false);
                        }
                      }}
                    >
                      <FaSync aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="chat-thread__icon-btn"
                      title="Close conversation"
                      aria-label="Close conversation"
                      onClick={handleLeaveConversation}
                    >
                      <FaTimes aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="chat-messages">
                  {messagesHasMore && (
                    <div className="chat-messages__load-older">
                      <button
                        type="button"
                        className="chat-messages__load-older-btn"
                        disabled={loadingOlderMessages || isLoadingMessages}
                        onClick={async () => {
                          if (!currentConversation?.conversationId) return;
                          const el = document.querySelector(".chat-messages");
                          const prevHeight = el?.scrollHeight || 0;
                          await loadOlderMessages(
                            currentConversation.conversationId
                          );
                          requestAnimationFrame(() => {
                            if (!el) return;
                            el.scrollTop = el.scrollHeight - prevHeight;
                          });
                        }}
                      >
                        {loadingOlderMessages
                          ? "Loading..."
                          : "Load earlier messages"}
                      </button>
                    </div>
                  )}
                  {isLoadingMessages && (
                    <div className="chat-messages__loading">
                      <div className="chat-messages__spinner" />
                    </div>
                  )}
                  {messages.map((message, index) => {
                    const mine = message.sender?._id === user._id;
                    const dayKey = getDayKey(message.createdAt);
                    const prevDayKey =
                      index > 0 ? getDayKey(messages[index - 1].createdAt) : null;
                    const showDateSeparator = dayKey && dayKey !== prevDayKey;

                    return (
                      <div key={message._id} className="chat-message-block">
                        {showDateSeparator && (
                          <div className="chat-date-separator" role="separator">
                            <span className="chat-date-separator__label">
                              {formatMessageDate(message.createdAt)}
                            </span>
                          </div>
                        )}
                        <div
                          className={`chat-bubble-row ${
                            mine
                              ? "chat-bubble-row--mine"
                              : "chat-bubble-row--theirs"
                          }`}
                        >
                          <div
                            className={`chat-bubble ${
                              mine ? "chat-bubble--mine" : "chat-bubble--theirs"
                            }`}
                          >
                            <p className="chat-bubble__text">{message.content}</p>
                            <p className="chat-bubble__time">
                              {formatTime(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <div className="chat-composer">
                  <form
                    onSubmit={handleSendMessage}
                    className="chat-composer__form"
                  >
                    <input
                      type="text"
                      value={messageInput}
                      onFocus={() => {
                        if (currentConversation) {
                          markAsRead(currentConversation.conversationId);
                        }
                      }}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder="Write a message…"
                      className="chat-composer__input"
                      disabled={!otherUser?._id}
                      aria-label="Message"
                    />
                    <button
                      type="submit"
                      className="chat-composer__send"
                      disabled={!messageInput.trim() || !otherUser?._id}
                      aria-label="Send message"
                    >
                      <FaPaperPlane aria-hidden="true" />
                    </button>
                  </form>
                  {!otherUser?._id && (
                    <p className="chat-composer__note">
                      This user was deleted. You can’t send new messages.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="chat-empty">
                <div className="chat-empty__mark" aria-hidden="true">
                  <FaComments />
                </div>
                <h3 className="chat-empty__title">Select a conversation</h3>
                <p className="chat-empty__text">
                  Choose someone from your inbox to keep the conversation going.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </PageWithRail>
  );
}

export default Chat;
